import Dexie from 'dexie';
import {
    type CallbackNode,
    unstable_cancelCallback as cancelCallback,
    unstable_LowPriority as LowPriority,
    unstable_scheduleCallback as scheduleCallback,
} from 'scheduler';

import { Logger, Trace } from '@gitbook/util-debug';
import { filterOutNullable } from '@gitbook/util-ts';

import type { SuspenceBrowserCache } from './browserCache';
import { type SuspenseCacheEffect, SuspenseCacheEntryState } from './cache';

const DEBOUNCE_TIMEOUT = 1000;
const CACHE_KEEPALIVE_PERIOD = 7 * 24 * 60 * 60 * 1000;

/**
 * Effect to store the cache in the browser IndexedDB.
 */
export function createBrowserCacheEffect<Result>(dbName: string): SuspenceBrowserCache<Result> {
    const debugName = `browsercache:${dbName}`;
    const logger = Logger(debugName);

    let initialized = false;

    const db = new Dexie(dbName);
    db.version(1).stores({
        // the value for the key is stored in the value property of the table
        entries: 'cacheKey,lastFlushedAt',
    });
    db.open().then(
        () => {
            initialized = true;
        },
        (error) => {
            logger.error('error while opening db', error);
        }
    );

    const entriesTable = db.table<{
        cacheKey: string;
        value: Result;
        lastFlushedAt: number;
    }>('entries');

    const pendingKeys = new Set<string>();

    let pendingTimeout: NodeJS.Timeout | null = null;
    let scheduledNode: CallbackNode | null = null;

    const clearCache = async () => {
        if (!initialized) {
            return;
        }

        try {
            logger.info('clear cache');
            pendingKeys.clear();
            await entriesTable.clear();
        } catch (error) {
            logger.error('error while clearing cache:', error);
        }
    };

    const effect: SuspenseCacheEffect<Result> = (cache) => {
        /**
         * Write all keys that changed to the database
         */
        async function flushToDatabase() {
            if (!initialized) {
                return;
            }

            logger.info(`flush ${pendingKeys.size} to the browser database`);

            const flushTrace = new Trace(`${debugName}.flush`, { sampleRate: 0 });
            await flushTrace.wrap(async () => {
                const now = Date.now();

                const keysToFlush = Array.from(pendingKeys.values());
                pendingKeys.clear();

                await entriesTable.bulkPut(
                    keysToFlush
                        .map((cacheKey) => {
                            const result = cache.getKey(cacheKey, true);
                            if (!result) {
                                return null;
                            }

                            // Ignore unset value in cache (ex: when currently fetching, etc)
                            const { state, value } = result;
                            if (state !== SuspenseCacheEntryState.LIVE || value === undefined) {
                                return null;
                            }

                            return {
                                cacheKey,
                                value,
                                lastFlushedAt: now,
                            };
                        })
                        .filter(filterOutNullable)
                );
            });

            const cleanupTrace = new Trace(`${debugName}.cleanup`, { sampleRate: 0 });
            await cleanupTrace.wrap(async () => {
                // Cleanup old entries
                await entriesTable
                    .where('lastFlushedAt')
                    .below(Date.now() - CACHE_KEEPALIVE_PERIOD)
                    .delete();
            });
        }

        /**
         * Debounce the flush to the database when a key has changed
         */
        function flushKeyToDatabase(key: string) {
            pendingKeys.add(key);

            if (pendingTimeout !== null) {
                clearTimeout(pendingTimeout);
            }

            if (scheduledNode !== null) {
                cancelCallback(scheduledNode);
            }

            pendingTimeout = setTimeout(() => {
                pendingTimeout = null;

                scheduledNode = scheduleCallback(LowPriority, () => {
                    flushToDatabase();
                    scheduledNode = null;
                });
            }, DEBOUNCE_TIMEOUT);
        }

        //
        // Hook to initialize a key from the cache when it is first read.
        //
        cache.onInitializeKey(async (key) => {
            if (!initialized) {
                return undefined;
            }

            const result = await entriesTable.get(key);
            if (!result) {
                return undefined;
            }

            logger.info(`initializing ${key} from browser cache`);
            return {
                result: result.value,
                setAt: result.lastFlushedAt,
            };
        });

        //
        // When keys are changing, we flush to the database.
        //
        cache.onSet((key) => {
            flushKeyToDatabase(key);
        });
    };

    return { effect, clearCache };
}
